import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import {
    Box, Button, TextField, Typography, Grid, Paper, CircularProgress, Alert,
    Tabs, Tab, InputAdornment, IconButton, Tooltip, Chip, MenuItem, Select, FormControl,
    InputLabel, Menu, Switch, Avatar, Autocomplete,
    Stack, Link
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LinkIcon from '@mui/icons-material/Link';
import TitleIcon from '@mui/icons-material/Title';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import NotesIcon from '@mui/icons-material/Notes';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ImageIcon from '@mui/icons-material/Image';
import CloseIcon from '@mui/icons-material/Close';
import { BioService, BIO_THEMES } from '../../../../core/services/bio.service';
import { BASE_URL_UI } from '../../../../config/config';
import { BioRender, socialIcon, SOCIAL_PLATFORMS } from './BioRender';
import { socialUrlError } from './bioValidation';
import BioWizard from './BioWizard';
import FeatureGuideModal from '../guide/FeatureGuideModal';
import GuideButton from '../guide/GuideButton';
import { LINK_IN_BIO_GUIDE } from '../guide/guideContent';
import { BioAnalytics } from './BioAnalytics';
import { StorefrontImageUpload } from '../../../../components/storefront-image-upload/storefront-image-upload';
import FileUploadPopup from '../../../../components/popup/file-upload-popup';
import { Language, InfoOutlined } from '@mui/icons-material';
import useBodyClass from '../../../../hooks/useBodyClass';
import ZapMobileHeader from '../../../../components/mobile/ZapMobileHeader';
import LinkinBioImage from '../../../../assets/images/link-in-bio.svg';
import { Link2Icon } from 'lucide-react';
import { creatorDisplayName } from '../../../../modules/influencer/flowUser';

interface Props { UserReducer: any; }

// Bordered icon tile matching the Zap Automations page header treatment.
const ZapPageIcon: React.FC = () => (
    <Box sx={{
        width: 60, height: 60, borderRadius: '10px', flexShrink: 0,
        border: '1px solid', borderColor: 'grey.200',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
        <img src={LinkinBioImage} alt="Zap Logo" width={36} height={36} />
    </Box>
);

const slugify = (s: string) => (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
// BASE_URL_UI without the http(s):// scheme — shown in the URL prefix/preview.
const BASE_HOST = (BASE_URL_UI || '').replace(/^https?:\/\//, '');
// BioWizard's two columns: the steps (max 720) + 24px gap + the 340px preview.
const BIO_SETUP_WIDTH = 720 + 24 + 340;

const BLOCK_TYPES = [
    { type: 'link', label: 'Link', icon: <LinkIcon fontSize="small" />, desc: 'Add a link to any web page' },
    { type: 'title', label: 'Title', icon: <TitleIcon fontSize="small" />, desc: 'A heading to organize your links' },
    { type: 'text', label: 'Text', icon: <NotesIcon fontSize="small" />, desc: 'A block of text' },
    { type: 'divider', label: 'Divider', icon: <HorizontalRuleIcon fontSize="small" />, desc: 'Split up your links with space' },
];
const parseJson = (v: any, fb: any) => { if (!v) return fb; if (typeof v === 'object') return v; try { return JSON.parse(v); } catch { return fb; } };

// NOTE: PLATFORM_DOMAINS + socialUrlError used to be defined here; they moved
// to bioValidation.ts (imported at the top) so BioWizard can use them too.

// Format check for the link-in-bio "link" block's destination URL — accepts a
// missing scheme (adds https://) but requires a real host made of dot-separated
// domain labels (a TLD), so junk like "asdf" or "http://a" is rejected. Plain text
// (e.g. pasted ad copy) can't be rejected by just checking the URL constructor
// doesn't throw: browsers IDNA/punycode-encode non-ASCII hostnames instead of
// throwing, so a whole sentence containing "." (e.g. "Terms apply. ... Apple.")
// gets accepted as a bogus hostname unless the label characters are checked too.
const isValidUrl = (url: string): boolean => {
    const trimmed = url.trim();
    if (/\s/.test(trimmed)) return false;
    try {
        const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
        const { hostname } = new URL(withScheme);
        return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i.test(hostname);
    } catch { return false; }
};

const LinkInBioComponent: React.FC<Props> = ({ UserReducer }) => {
    useBodyClass('zap-inner-page');
    const influencer_id = UserReducer?.influencer_id;
    const account_id = UserReducer?.account_id;
    const user_id = UserReducer?.user_id;
    const uploadPath = `accounts/${account_id}/influencer/${influencer_id}/bio`;

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState(0); // 0 Links, 1 Appearance, 2 Analytics
    const [guideOpen, setGuideOpen] = useState(false);
    const [alert, setAlert] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
    const [page, setPage] = useState<any>(null);
    const [blocks, setBlocks] = useState<any[]>([]);
    const [linkCopied, setLinkCopied] = useState(false);
    const [slugChecking, setSlugChecking] = useState(false);
    const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
    // True once the user has typed/regenerated their own handle — stops the
    // username default from overwriting their choice.
    const [slugTouched, setSlugTouched] = useState(false);

    const [form, setForm] = useState<any>({
        slug: '', display_name: '', headline: '',
        avatar_url: '', avatar_file_name: '', avatar_file_type: '', avatar_file_path: '', avatar_storage_size: '',
        social_links: [] as any[], theme: { ...BIO_THEMES.light, button_shape: 'rounded' },
    });

    const [addMenuEl, setAddMenuEl] = useState<null | HTMLElement>(null);
    const [uploadBlockId, setUploadBlockId] = useState<number | null>(null);
    const [dragIdx, setDragIdx] = useState<number | null>(null);
    const [adding, setAdding] = useState(false);

    const blockTimers = useRef<Record<string, any>>({});
    const profileTimer = useRef<any>(null);
    const bio_page_id = page?.bio_page_id;

    // auto-save status indicator (since there is no explicit Save button)
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
    const savedTimer = useRef<any>(null);
    const markSaving = () => { clearTimeout(savedTimer.current); setSaveStatus('saving'); };
    const markSaved = () => { setSaveStatus('saved'); clearTimeout(savedTimer.current); savedTimer.current = setTimeout(() => setSaveStatus('idle'), 2000); };

    const hydrate = (p: any, blks: any[]) => {
        setPage(p);
        setBlocks(blks || []);
        const t = parseJson(p.theme, { ...BIO_THEMES.light, button_shape: 'rounded' });
        const s = parseJson(p.social_links, []);
        setForm({
            slug: p.slug || '', display_name: p.display_name || '', headline: p.headline || '',
            avatar_url: p.avatar_url || '', avatar_file_name: p.avatar_file_name || '', avatar_file_type: p.avatar_file_type || '',
            avatar_file_path: p.avatar_file_path || '', avatar_storage_size: p.avatar_storage_size || '',
            social_links: Array.isArray(s) ? s : [], theme: { ...BIO_THEMES.light, button_shape: 'rounded', ...t },
        });
    };

    useEffect(() => {
        if (!influencer_id || !account_id) { setLoading(false); return; }
        BioService.getMyBioPage(influencer_id, account_id)
            .then((res: any) => { if (res?.data?.rows?.length) hydrate(res.data.rows[0], res.data.blocks || []); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [influencer_id, account_id]);

    // ---------- slug (first-time only) ----------
    const checkSlug = (slug: string) => {
        setSlugAvailable(null);
        if (!slug || slug.length < 3) return;
        setSlugChecking(true);
        BioService.checkSlug(slug, influencer_id, account_id)
            .then((res: any) => setSlugAvailable(res?.data?.available ?? null))
            .catch(() => setSlugAvailable(null)).finally(() => setSlugChecking(false));
    };
    // First-time setup: pre-fill the handle with the influencer's own username so
    // it's ready to use as-is. They can freely edit it afterwards (slugTouched),
    // and for an existing page we never auto-change the saved slug.
    useEffect(() => {
        if (page || slugTouched) return;
        const base = slugify(creatorDisplayName(UserReducer) || '')
            || slugify(form.display_name);
        if (!base) return;
        setForm((f: any) => (f.slug === base ? f : { ...f, slug: base }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [UserReducer, form.display_name, page, slugTouched]);

    // Check availability whenever the handle changes (debounced). Skip re-checking
    // an existing page's own saved slug.
    useEffect(() => {
        const s = form.slug;
        if (page && s === page.slug) { setSlugAvailable(null); return; }
        if (!s || s.length < 3) { setSlugAvailable(null); return; }
        const t = setTimeout(() => checkSlug(s), 400);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.slug, page]);

    const regenerateSlug = () => {
        const base = slugify(creatorDisplayName(UserReducer) || form.display_name) || 'me';
        const candidate = `${base}-${Math.random().toString(36).slice(2, 6)}`;
        setSlugTouched(true);
        patchForm({ slug: candidate });
    };

    // ---------- profile save (create button first-time, debounced auto-save after) ----------
    // OLD: BioService.setupBioPage({ ...nextForm, influencer_id, account_id, user_id });
    // brand_id is undefined for influencers and set for brand users (this screen serves
    // both, via /influencer/zap/link-in-bio and /brand/link-in-bio). The backend uses it
    // to record owner_type when it first registers this user's cctm_id.
    const persistProfile = (nextForm: any) => BioService.setupBioPage({ ...nextForm, influencer_id, account_id, user_id, brand_id: UserReducer?.brand_id || null });
    const patchForm = (patch: any) => {
        setForm((f: any) => {
            const nf = { ...f, ...patch };
            if (page) { // auto-save existing page
                markSaving();
                clearTimeout(profileTimer.current);
                profileTimer.current = setTimeout(async () => { await persistProfile(nf); markSaved(); }, 700);
            }
            return nf;
        });
    };
    // OLD: first-time creation button handler. BioWizard now owns creation (it
    // calls BioService.setupBioPage itself and hands back through onCreated),
    // so nothing calls this any more.
    // const createPage = async () => {
    //     if (!form.display_name || !form.slug) { setAlert({ type: 'error', msg: 'Name and handle are required' }); return; }
    //     setSaving(true); setAlert(null);
    //     try {
    //         const res = await persistProfile(form);
    //         if (res?.status === 1 && res?.data?.rows?.length) {
    //             const fresh = await BioService.getMyBioPage(influencer_id, account_id);
    //             hydrate(fresh.data.rows[0], fresh.data.blocks || []);
    //             setAlert({ type: 'success', msg: 'Your Link-in-Bio is live!' });
    //         } else setAlert({ type: 'error', msg: res?.info || 'Failed to create' });
    //     } catch { setAlert({ type: 'error', msg: 'Something went wrong' }); }
    //     finally { setSaving(false); }
    // };

    // ---------- social ----------
    // Predefined platforms are picked from a fixed list (name not editable). "Other" reveals
    // a free-text field so the user can name any custom platform. `otherRows` tracks rows the
    // user explicitly switched to "Other" but hasn't named yet (platform still empty).
    const [otherRows, setOtherRows] = useState<Set<number>>(new Set());
    // Canonical predefined name for a stored value (case-insensitive), or undefined if custom.
    const matchPredefined = (platform: string) =>
        SOCIAL_PLATFORMS.find((p) => p.toLowerCase() === (platform || '').toLowerCase().trim());
    const isOtherRow = (i: number, platform: string) =>
        otherRows.has(i) || (!!platform && !matchPredefined(platform));
    const handlePlatformSelect = (i: number, val: string) => {
        if (val === 'Other') {
            setOtherRows((prev) => new Set(prev).add(i));
            setSocial(i, 'platform', '');
        } else {
            setOtherRows((prev) => { const n = new Set(prev); n.delete(i); return n; });
            setSocial(i, 'platform', val);
        }
    };
    const addSocial = () => patchForm({ social_links: [...form.social_links, { platform: '', url: '' }] });
    const setSocial = (i: number, key: string, val: string) => { const a = [...form.social_links]; a[i] = { ...a[i], [key]: val }; patchForm({ social_links: a }); };
    const removeSocial = (i: number) => {
        patchForm({ social_links: form.social_links.filter((_: any, idx: number) => idx !== i) });
        // Keep otherRows indices aligned after the row is removed.
        setOtherRows((prev) => {
            const next = new Set<number>();
            prev.forEach((idx) => { if (idx < i) next.add(idx); else if (idx > i) next.add(idx - 1); });
            return next;
        });
    };

    // ---------- blocks ----------
    const reloadBlocks = async () => { const fresh = await BioService.getMyBioPage(influencer_id, account_id); if (fresh?.data?.rows?.length) setBlocks(fresh.data.blocks || []); };

    // A link block with a blank/malformed url is kept in local state so the user can keep
    // typing, but is never persisted — otherwise junk text gets saved and rendered as a
    // broken href on the public bio page.
    const hasInvalidLinkUrl = (b: any) => b.block_type === 'link' && (!b.url?.trim() || !isValidUrl(b.url));
    const scheduleBlockSave = (b: any) => {
        if (hasInvalidLinkUrl(b)) return;
        markSaving();
        clearTimeout(blockTimers.current['b' + b.block_id]);
        blockTimers.current['b' + b.block_id] = setTimeout(async () => {
            await BioService.updateBlock({ block_id: b.block_id, title: b.title, url: b.url, image_url: b.image_url, is_active: b.is_active, config: parseJson(b.config, null) });
            markSaved();
        }, 600);
    };
    const editBlock = (block_id: number, patch: any, immediate = false) => {
        setBlocks(prev => prev.map(b => {
            if (b.block_id !== block_id) return b;
            const nb = { ...b, ...patch };
            if (hasInvalidLinkUrl(nb)) { clearTimeout(blockTimers.current['b' + block_id]); return nb; }
            if (immediate) { markSaving(); BioService.updateBlock({ block_id, title: nb.title, url: nb.url, image_url: nb.image_url, is_active: nb.is_active, config: parseJson(nb.config, null) }).then(markSaved); }
            else scheduleBlockSave(nb);
            return nb;
        }));
    };
    
    const addBlock = async (block_type: string) => {
        setAddMenuEl(null);
        const config = block_type === 'divider' ? { height: 'small' } : undefined;
        // One round-trip: the add endpoint returns the created row, prepend it so
        // new blocks show at the top, then persist the new order so it sticks on reload.
        setAdding(true); markSaving();
        try {
            const res = await BioService.addBlock({ bio_page_id, block_type, title: '', url: '', image_url: '', config });
            if (res?.status === 1 && res?.data?.rows?.length) {
                const newArr = [res.data.rows[0], ...blocks];
                setBlocks(newArr);
                await BioService.reorderBlocks({ bio_page_id, order: newArr.map(b => b.block_id) });
                markSaved();
            }
            else setAlert({ type: 'error', msg: res?.info || 'Failed to add block' });
        } catch { setAlert({ type: 'error', msg: 'Failed to add block' }); }
        finally { setAdding(false); }
    };
    const removeBlock = async (block_id: number) => {
        setBlocks(prev => prev.filter(b => b.block_id !== block_id));
        await BioService.deleteBlock({ bio_page_id, block_id });
    };

    // native HTML5 drag reorder
    const onDrop = async (targetIdx: number) => {
        if (dragIdx === null || dragIdx === targetIdx) { setDragIdx(null); return; }
        const arr = [...blocks];
        const [moved] = arr.splice(dragIdx, 1);
        arr.splice(targetIdx, 0, moved);
        setBlocks(arr);
        setDragIdx(null);
        await BioService.reorderBlocks({ bio_page_id, order: arr.map(b => b.block_id) });
    };

    const publicUrl = page ? `${BASE_URL_UI}/bio/${page.slug}` : '';
    // Scheme-stripped version for display only (copy/open still use the full URL).
    const publicUrlDisplay = publicUrl.replace(/^https?:\/\//, '');
    const copyLink = async () => { try { await navigator.clipboard.writeText(publicUrl); setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2000); } catch {} };
    if (loading) return <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>;

    // ===== block card (inline editable) =====
    const blockCard = (b: any, idx: number) => {
        const dividerH = parseJson(b.config, {})?.height || 'small';
        // return (
        //     <Paper key={b.block_id} variant="outlined"
        //         onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(idx)}
        //         sx={{ p: 1.25, mb: 1.25, opacity: dragIdx === idx ? 0.4 : 1, borderColor: dragIdx === idx ? 'primary.main' : undefined }}>
        //         <Box display="flex" gap={1} alignItems="flex-start">
        //             <Box draggable onDragStart={() => setDragIdx(idx)} onDragEnd={() => setDragIdx(null)}
        //                 sx={{ cursor: 'grab', color: 'text.disabled', pt: 0.5 }}><DragIndicatorIcon fontSize="small" /></Box>

        //             <Box flexGrow={1} minWidth={0}>
        //                 {b.block_type === 'link' && (
        //                     <>
        //                         <TextField fullWidth size="small" placeholder="Add your link label" value={b.title}
        //                             onChange={e => editBlock(b.block_id, { title: e.target.value })} sx={{ mb: 1 }} />
        //                         <TextField fullWidth size="small" placeholder="Add a link to any web page" value={b.url}
        //                             onChange={e => editBlock(b.block_id, { url: e.target.value })}
        //                             InputProps={{ startAdornment: <InputAdornment position="start"><LinkIcon sx={{ fontSize: 16 }} /></InputAdornment> }} />
        //                     </>
        //                 )}
        //                 {b.block_type === 'title' && (
        //                     <TextField fullWidth size="small" placeholder="Section title" value={b.title}
        //                         onChange={e => editBlock(b.block_id, { title: e.target.value })} />
        //                 )}
        //                 {b.block_type === 'text' && (
        //                     <TextField fullWidth size="small" multiline rows={2} placeholder="Write some text…" value={b.title}
        //                         onChange={e => editBlock(b.block_id, { title: e.target.value })} />
        //                 )}
        //                 {b.block_type === 'divider' && (
        //                     <Box>
        //                         <Box sx={{ borderTop: '1px solid #ccc', my: 1 }} />
        //                         <Box display="flex" gap={0.5} alignItems="center">
        //                             <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Height:</Typography>
        //                             {['small', 'medium', 'large'].map(h => (
        //                                 <Chip key={h} size="small" label={h} variant={dividerH === h ? 'filled' : 'outlined'}
        //                                     color={dividerH === h ? 'primary' : 'default'} sx={{ textTransform: 'capitalize' }}
        //                                     onClick={() => editBlock(b.block_id, { config: { height: h } }, true)} />
        //                             ))}
        //                         </Box>
        //                     </Box>
        //                 )}

        //                 {/* toolbar */}
        //                 <Box display="flex" alignItems="center" gap={0.5} mt={0.75}>
        //                     <Tooltip title={b.is_active ? 'Visible' : 'Hidden'}>
        //                         <Switch size="small" checked={b.is_active === 1 || b.is_active === true}
        //                             onChange={e => editBlock(b.block_id, { is_active: e.target.checked ? 1 : 0 }, true)} />
        //                     </Tooltip>
        //                     <Chip size="small" label={b.block_type} sx={{ textTransform: 'capitalize' }} />
        //                     <Box flexGrow={1} />
        //                     <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => removeBlock(b.block_id)}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
        //                 </Box>
        //             </Box>

        //             {/* thumbnail (link only) */}
        //             {b.block_type === 'link' && (
        //                 <Box sx={{ width: 56, flexShrink: 0, textAlign: 'center' }}>
        //                     {b.image_url ? (
        //                         <Box position="relative">
        //                             <Avatar variant="rounded" src={b.image_url} sx={{ width: 56, height: 56 }} />
        //                             <IconButton size="small" onClick={() => editBlock(b.block_id, { image_url: '' }, true)}
        //                                 sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#fff', boxShadow: 1, p: 0.25 }}><CloseIcon sx={{ fontSize: 14 }} /></IconButton>
        //                         </Box>
        //                     ) : (
        //                         <Tooltip title="Add image">
        //                             <Box onClick={() => setUploadBlockId(b.block_id)}
        //                                 sx={{ width: 56, height: 56, border: '1px dashed #bbb', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'text.disabled' }}>
        //                                 <ImageIcon fontSize="small" />
        //                             </Box>
        //                         </Tooltip>
        //                     )}
        //                 </Box>
        //             )}
        //         </Box>
        //     </Paper>
        // );
        return (
    <Paper
    key={b.block_id}
    elevation={0}
    onDragOver={(e) => e.preventDefault()}
    onDrop={() => onDrop(idx)}
    sx={{
        mb: 3,
        borderRadius: 4,
        border: "1px solid #E5E7EB",
        overflow: "hidden",
        transition: ".25s",
        bgcolor: "#fff",

        "&:hover": {
            borderColor: "#4F46E5",
            boxShadow: "0 18px 45px rgba(79,70,229,.08)",
            transform: "translateY(-2px)",
        },
    }}
>
        {/* Header */}

    <Box
        sx={{
            px: 3,
            py: 2,
            borderBottom: "1px solid #EEF2F7",
            display: "flex",
            alignItems: "center",
        }}
    >
        <Box
            draggable
            onDragStart={() => setDragIdx(idx)}
            onDragEnd={() => setDragIdx(null)}
            sx={{
                mr: 2,
                color: "#94A3B8",
                cursor: "grab",
            }}
        >
            <DragIndicatorIcon />
        </Box>

        <Chip
            label={b.block_type}
            color="primary"
            size="small"
            sx={{
                textTransform: "capitalize",
                fontWeight: 700,
            }}
        />

        <Box flexGrow={1} />

        <Typography
            variant="body2"
            color="text.secondary"
            mr={2}
            sx={{ display: { xs: 'none', sm: 'block' } }}
        >
            {b.is_active ? "Visible" : "Hidden"}
        </Typography>

        <Switch
            checked={b.is_active === 1 || b.is_active === true}
            onChange={(e) =>
                editBlock(
                    b.block_id,
                    {
                        is_active: e.target.checked ? 1 : 0,
                    },
                    true
                )
            }
        />

        <IconButton
            color="error"
            onClick={() => removeBlock(b.block_id)}
            sx={{ ml: 1 }}
        >
            <DeleteOutlineIcon />
        </IconButton>
    </Box>

    {/* Body */}

        {/* <Box p={3}>

            {b.block_type === "link" && (

                <Grid container spacing={3}>

                    <Grid item xs={12} md={8}>

                        <TextField
                            fullWidth
                            label="Button Title"
                            value={b.title}
                            onChange={(e) =>
                                editBlock(b.block_id, {
                                    title: e.target.value,
                                })
                            }
                            sx={{ mb: 2 }}
                        />

                        <TextField
                            fullWidth
                            label="Destination URL"
                            value={b.url}
                            placeholder="https://..."
                            onChange={(e) =>
                                editBlock(b.block_id, {
                                    url: e.target.value,
                                })
                            }
                        />

                    </Grid>

                    <Grid item xs={12} md={4}>

                        {b.image_url ? (

                            <Box
                                sx={{
                                    position: "relative",
                                    borderRadius: 3,
                                    overflow: "hidden",
                                }}
                            >
                                <img
                                    src={b.image_url}
                                    alt=""
                                    style={{
                                        width: "100%",
                                        height: 160,
                                        objectFit: "cover",
                                    }}
                                />

                                <IconButton
                                    onClick={() =>
                                        editBlock(
                                            b.block_id,
                                            {
                                                image_url: "",
                                            },
                                            true
                                        )
                                    }
                                    sx={{
                                        position: "absolute",
                                        top: 10,
                                        right: 10,
                                        bgcolor: "#fff",
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>

                            </Box>

                        ) : (

                            <Paper
                                variant="outlined"
                                onClick={() =>
                                    setUploadBlockId(
                                        b.block_id
                                    )
                                }
                                sx={{
                                    height: 160,
                                    borderRadius: 3,
                                    borderStyle: "dashed",
                                    cursor: "pointer",

                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",

                                    "&:hover": {
                                        borderColor:
                                            "#2563EB",
                                    },
                                }}
                            >
                                <ImageIcon
                                    sx={{
                                        fontSize: 40,
                                        color: "#94A3B8",
                                    }}
                                />

                                <Typography
                                    mt={1}
                                    color="text.secondary"
                                >
                                    Upload Thumbnail
                                </Typography>

                            </Paper>

                        )}

                    </Grid>

                </Grid>

            )}

            {b.block_type === "title" && (

                <TextField
                    fullWidth
                    label="Section Title"
                    value={b.title}
                    onChange={(e) =>
                        editBlock(b.block_id, {
                            title: e.target.value,
                        })
                    }
                />

            )}

            {b.block_type === "text" && (

                <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Text"
                    value={b.title}
                    onChange={(e) =>
                        editBlock(b.block_id, {
                            title: e.target.value,
                        })
                    }
                />

            )}

            {b.block_type === "divider" && (

                <Stack
    direction="row"
    spacing={1}
    mt={2}
>
                    {["small", "medium", "large"].map((h) => (

                        <Chip
                            key={h}
                            clickable
                            label={h}
                            color={
                                (parseJson(
                                    b.config,
                                    {}
                                )?.height || "small") === h
                                    ? "primary"
                                    : "default"
                            }
                            onClick={() =>
                                editBlock(
                                    b.block_id,
                                    {
                                        config: {
                                            height: h,
                                        },
                                    },
                                    true
                                )
                            }
                        />

                    ))}
                </Stack>

            )}

        </Box> */}
        <Box
        sx={{
            p: 3,
            display: "flex",
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 3,
        }}
    >
        {/* Left */}

        <Box flex={1}>
             {b.block_type === 'link' && (
                            <>
                                <TextField fullWidth size="small" placeholder="Add your link label" value={b.title}
                                    onChange={e => editBlock(b.block_id, { title: e.target.value })}
                                    error={!b.title?.trim()} helperText={!b.title?.trim() ? 'Label cannot be blank' : ' '} sx={{ mb: 1 }} />
                                {b.source_type === 'cashkaro' ? (
                                    // Cross-posted from Affiliate Deals — b.url is the raw Trackier
                                    // tracking_url (needed server-side to resolve /bio/r/:click_token),
                                    // never shown or hand-editable, same as Affiliate Deals itself only
                                    // ever surfaces the click_token link.
                                    <TextField fullWidth size="small" value={BioService.clickUrl(b.click_token)}
                                        disabled
                                        helperText="Tracked Affiliate Deals link — managed automatically"
                                        InputProps={{ startAdornment: <InputAdornment position="start"><LinkIcon sx={{ fontSize: 16 }} /></InputAdornment> }} />
                                ) : (
                                    <TextField fullWidth size="small" placeholder="Add a link to any web page" value={b.url}
                                        onChange={e => editBlock(b.block_id, { url: e.target.value })}
                                        error={!b.url?.trim() || !isValidUrl(b.url)}
                                        helperText={!b.url?.trim() ? 'Link cannot be blank' : !isValidUrl(b.url) ? 'Enter a valid URL' : ' '}
                                        InputProps={{ startAdornment: <InputAdornment position="start"><LinkIcon sx={{ fontSize: 16 }} /></InputAdornment> }} />
                                )}
                            </>
                        )}
                        {b.block_type === 'title' && (
                            <TextField fullWidth size="small" placeholder="Section title" value={b.title}
                                onChange={e => editBlock(b.block_id, { title: e.target.value })}
                                error={!b.title?.trim()} helperText={!b.title?.trim() ? 'Title cannot be blank' : ' '} />
                        )}
                        {b.block_type === 'text' && (
                            <TextField fullWidth size="small" multiline rows={2} placeholder="Write some text…" value={b.title}
                                onChange={e => editBlock(b.block_id, { title: e.target.value })}
                                error={!b.title?.trim()} helperText={!b.title?.trim() ? 'Text cannot be blank' : ' '} />
                        )}
                        {b.block_type === 'divider' && (
                            <Box>
                                <Box sx={{ borderTop: '1px solid #ccc', my: 1 }} />
                                <Box display="flex" gap={0.5} alignItems="center">
                                    <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Height:</Typography>
                                    {['small', 'medium', 'large'].map(h => (
                                        <Chip key={h} size="small" label={h} variant={dividerH === h ? 'filled' : 'outlined'}
                                            color={dividerH === h ? 'primary' : 'default'} sx={{ textTransform: 'capitalize' }}
                                            onClick={() => editBlock(b.block_id, { config: { height: h } }, true)} />
                                    ))}
                                </Box>
                            </Box>
                        )}
        </Box>

        {/* Right */}

        {b.block_type === "link" && (
            <Box sx={{ width: { xs: '100%', sm: 90 }, flexShrink: 0 }}>

                {b.image_url ? (

                    <Box position="relative" sx={{ width: { xs: 100, sm: 90 }, height: { xs: 'auto', sm: 90 } }}>

                        <Avatar
                            src={b.image_url}
                            variant="rounded"
                            sx={{
                                width: { xs: '100%', sm: 90 },
                                height: { xs: 'auto', sm: 90 },
                                borderRadius: 3,
                                objectFit:'contain',
                                border: "1px solid #E5E7EB",
                            }}
                        />

                        <IconButton
                            size="small"  
                            onClick={() =>
                                editBlock(
                                    b.block_id,
                                    {
                                        image_url: "",
                                    },
                                    true
                                )
                            }
                            sx={{
                                position: "absolute",
                                top: -10,
                                right: -10,
                                bgcolor: "#fff",
                                border: "1px solid #E5E7EB",
                            }}
                        >
                            <CloseIcon fontSize="small" sx={{fontSize:14}} />
                        </IconButton>

                    </Box>

                ) : (

                    <Paper
                        elevation={0}
                        onClick={() =>
                            setUploadBlockId(
                                b.block_id
                            )
                        }
                        sx={{
                            height: { xs: 80, sm: 90 },
                            borderRadius: 3,
                            border: "2px dashed #CBD5E1",
                            display: "flex",
                            flexDirection: { xs: 'row', sm: 'column' },
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 1,
                            cursor: "pointer",

                            "&:hover": {
                                borderColor: "#4F46E5",
                            },
                        }}
                    >
                        <ImageIcon
                            sx={{
                                fontSize: 32,
                                color: "#6366F1",
                            }}
                        />

                        <Typography
                            fontSize={12}
                            mt={1}
                        >
                            Upload Image
                        </Typography>

                    </Paper>

                )}

            </Box>
        )}

    </Box>
    </Paper>
);
    };

    // ===== Links tab editor =====
    const linksEditor = (
        <Box>
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', md: 'center' }}
                spacing={2}
                mb={2}
            >
                <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="h5" fontWeight={700}>Links</Typography>
                        <Tooltip title="Add, reorder and manage the link blocks shown on your page.">
                            <InfoOutlined sx={{ fontSize: 18, color: 'text.secondary', cursor: 'pointer' }} />
                        </Tooltip>
                    </Stack>
                    <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                        Build your page with links, titles, text and dividers.
                    </Typography>
                </Box>

                <Button variant="contained" disabled={adding}
                    startIcon={adding ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                    onClick={(e) => setAddMenuEl(e.currentTarget)}
                    sx={{
                        bgcolor: '#000', color: '#fff', borderRadius: '10px', textTransform: 'none',
                        fontWeight: 600, px: 3, height: 40, whiteSpace: 'nowrap',
                        width: { xs: '100%', md: 'auto' },
                        '&:hover': { bgcolor: '#111' },
                    }}
                >{adding ? 'Adding…' : 'Add block'}</Button>
                <Menu anchorEl={addMenuEl} open={!!addMenuEl} onClose={() => setAddMenuEl(null)}>
                    {BLOCK_TYPES.map(bt => (
                        <MenuItem key={bt.type} onClick={() => addBlock(bt.type)} sx={{ py: 1 }}>
                            <Box display="flex" alignItems="center" gap={1.5}>
                                {bt.icon}
                                <Box>
                                    <Typography variant="body2" fontWeight={600}>{bt.label}</Typography>
                                    <Typography variant="caption" color="text.secondary">{bt.desc}</Typography>
                                </Box>
                            </Box>
                        </MenuItem>
                    ))}
                </Menu>
            </Stack>
            {blocks.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                    No blocks yet. Click “Add block” to add your first link.
                </Paper>
            ) : blocks.map((b, idx) => blockCard(b, idx))}
            {adding && (
                <Paper variant="outlined" sx={{ p: 2, mb: 1.25, display: 'flex', alignItems: 'center', gap: 1.5, color: 'text.secondary' }}>
                    <CircularProgress size={18} /> <Typography variant="body2">Adding block…</Typography>
                </Paper>
            )}
        </Box>
    );

    // ===== Appearance tab =====
    const appearance = (
    //     <Paper
    //         elevation={0}
    //         sx={{
    //             p: 4,
    //             borderRadius: 4,
    //             border: "1px solid #E5E7EB",
    //             background: "#fff",
    //             mb: 4,
    //         }}
    //     >
    //          <Typography
    //     variant="h5"
    //     fontWeight={800}
    // >Profile</Typography>
    //   <Typography
    //     variant="body2"
    //     color="text.secondary"
    //     mb={2}
    // >
    //     This information appears at the top of your Link in Bio page.
    // </Typography>
    // <Grid
    //     container
    //     spacing={4}
    //     alignItems="center"
    // >


    // </Grid>
    //         <Box mb={2}>
    //             <StorefrontImageUpload label="Avatar" value={form.avatar_url} uploadPath={uploadPath} previewVariant="logo" hint="Square image, PNG/JPG, max 2MB"
    //                 onChange={(m: any) => patchForm({ avatar_url: m.url, avatar_file_name: m.file_name, avatar_file_type: m.file_type, avatar_file_path: m.file_path, avatar_storage_size: m.storage_size })}
    //                 onRemove={() => patchForm({ avatar_url: '', avatar_file_name: '', avatar_file_type: '', avatar_file_path: '', avatar_storage_size: '' })} />
    //         </Box>
    //         <Grid container spacing={2}>
    //             <Grid item xs={12} sm={6}><TextField fullWidth label="Display name *" value={form.display_name} onChange={e => patchForm({ display_name: e.target.value })} /></Grid>
    //             <Grid item xs={12} sm={6}>
    //                 <TextField fullWidth label="Handle *" value={form.slug}
    //                     sx={{ '& .MuiInputBase-root': { bgcolor: '#F5F5F5' }, '& .MuiInputBase-input': { color: 'rgba(0,0,0,0.55)' } }}
    //                     InputProps={{ readOnly: true, startAdornment: <InputAdornment position="start">/bio/</InputAdornment>,
    //                         endAdornment: <InputAdornment position="end"><Tooltip title="Generate a different handle"><IconButton edge="end" size="small" onClick={regenerateSlug}><AutorenewIcon fontSize="small" /></IconButton></Tooltip></InputAdornment> }}
    //                     error={slugAvailable === false}
    //                     helperText={slugChecking ? 'Checking…' : slugAvailable === true ? '✓ Available' : slugAvailable === false ? '✗ Taken — click ↻' : 'Your public handle'} />
    //             </Grid>
    //             <Grid item xs={12}><TextField fullWidth label="Headline / bio" value={form.headline} onChange={e => patchForm({ headline: e.target.value })} placeholder="Welcome to my page!" /></Grid>
    //         </Grid>

    //         <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>Social icons — pick a platform or type your own</Typography>
    //         {form.social_links.map((s: any, i: number) => (
    //             <Box key={i} display="flex" gap={1} mt={1} alignItems="center">
    //                 <Autocomplete freeSolo disableClearable options={SOCIAL_PLATFORMS}
    //                     inputValue={s.platform || ''}
    //                     onInputChange={(_, val) => setSocial(i, 'platform', val)}
    //                     sx={{ width: 200 }}
    //                     renderOption={(props, option) => (
    //                         <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    //                             {socialIcon(option)} {option}
    //                         </Box>
    //                     )}
    //                     renderInput={(params) => (
    //                         <TextField {...params} size="small" placeholder="Platform"
    //                             InputProps={{ ...params.InputProps, startAdornment: <InputAdornment position="start">{socialIcon(s.platform)}</InputAdornment> }} />
    //                     )}
    //                 />
    //                 <TextField size="small" fullWidth placeholder="https://..." value={s.url} onChange={e => setSocial(i, 'url', e.target.value)} />
    //                 <IconButton size="small" color="error" onClick={() => removeSocial(i)}><DeleteOutlineIcon fontSize="small" /></IconButton>
    //             </Box>
    //         ))}
    //         <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={addSocial} sx={{ mt: 1 }}>Add social</Button>

    //         <Typography variant="caption" color="text.secondary" sx={{ mt: 2.5, display: 'block' }}>Theme</Typography>
    //         <Box display="flex" gap={1} mt={1} flexWrap="wrap" alignItems="center">
    //             {Object.entries(BIO_THEMES).map(([key, t]) => (
    //                 <Tooltip key={key} title={t.label}>
    //                     <Box onClick={() => patchForm({ theme: { ...t, button_shape: form.theme.button_shape } })}
    //                         sx={{ width: 34, height: 34, borderRadius: '50%', bgcolor: t.bg, border: form.theme.bg === t.bg ? '2px solid #2563EB' : '1px solid #ccc', cursor: 'pointer', position: 'relative' }}>
    //                         <Box sx={{ position: 'absolute', inset: 8, borderRadius: '50%', bgcolor: t.accent }} />
    //                     </Box>
    //                 </Tooltip>
    //             ))}
    //             <FormControl size="small" sx={{ minWidth: 130, ml: 1 }}>
    //                 <InputLabel>Buttons</InputLabel>
    //                 <Select label="Buttons" value={form.theme.button_shape || 'rounded'} onChange={e => patchForm({ theme: { ...form.theme, button_shape: e.target.value } })}>
    //                     <MenuItem value="rounded">Rounded</MenuItem><MenuItem value="pill">Pill</MenuItem><MenuItem value="square">Square</MenuItem>
    //                 </Select>
    //             </FormControl>
    //         </Box>
    //     </Paper>
    <>
    <Paper
    elevation={0}
    sx={{
        p: 2.5,
        borderRadius: 4,
        border: "1px solid #E5E7EB",
        background: "#fff",
        mb: 2,
        maxWidth:'1300px'
    }}
>

    <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
        <Typography
            variant="h5"
            fontWeight={700}
            sx={{ fontSize: { xs: 18, md: 20 } }}
        >
            Profile
        </Typography>

        <Tooltip title="This information appears at the top of your Link in Bio page.">
            <InfoOutlined sx={{ fontSize: 18, color: 'text.secondary', cursor: 'pointer' }} />
        </Tooltip>
    </Stack>

    <Grid
        container
        spacing={{
            xs: 2,
            md: 3,
            lg: 2,
        }}
        alignItems="center"
        justifyContent="space-between"
    >

        {/* Avatar */}

        <Grid
            item
            xs={12}
            lg={4}
        >

            <Box
                sx={{
                    display: "flex",
                    gap: 2,
                    alignItems: "flex-start",
                    flexDirection: {
                    xs: "column",
                    lg: "row",
                    },
                }}
            >

                {/* <Box
                    sx={{
                        width: 160,
                        height: 160,
                        borderRadius: "50%",
                        overflow: "hidden",
                        border: "5px solid #EEF2FF",
                        bgcolor: "#F8FAFC",
                        boxShadow:
                            "0 20px 40px rgba(37,99,235,.15)",
                    }}
                >

                    {form.avatar_url ? (
                        <img
                            src={form.avatar_url}
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                            }}
                        />
                    ) : (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            height="100%"
                        >
                            <Typography
                                variant="h2"
                            >
                                👤
                            </Typography>
                        </Box>
                    )}

                </Box> */}

                <Box
                    sx={{
                    width: {
                        xs: "100%",
                    },
                    flexShrink: 0,
                    display: "flex",
                    justifyContent: "center",
                    }}
                >

                    <StorefrontImageUpload
                        label="Avatar"
                        value={form.avatar_url}
                        uploadPath={uploadPath}
                        previewVariant="logo"
                        hint="PNG • JPG • Max 2 MB"
                        onChange={(m:any)=>
                            patchForm({
                                avatar_url:m.url,
                                avatar_file_name:m.file_name,
                                avatar_file_type:m.file_type,
                                avatar_file_path:m.file_path,
                                avatar_storage_size:m.storage_size
                            })
                        }
                        onRemove={()=>patchForm({
                            avatar_url:"",
                            avatar_file_name:"",
                            avatar_file_type:"",
                            avatar_file_path:"",
                            avatar_storage_size:""
                        })}
                    />

                </Box>

            </Box>

        </Grid>

        {/* Details */}

        <Grid
            item
            xs={12}
            lg={7}
        >

            <Grid
                container
                spacing={2}
            >

                <Grid item xs={12}>
                    <Stack spacing={1}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <Typography variant="body2" fontWeight={500} sx={{ fontSize: 12 }}>
                                Profile Name
                            </Typography>
                            <Tooltip title="This name is displayed at the top of your Link in Bio page.">
                                <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'pointer' }} />
                            </Tooltip>
                        </Stack>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Enter your display name"
                            value={form.display_name}
                            onChange={(e)=>
                                patchForm({
                                    display_name:e.target.value
                                })
                            }
                            error={!form.display_name?.trim()}
                            helperText={!form.display_name?.trim() ? 'Profile name cannot be blank' : ' '}
                        />
                    </Stack>
                </Grid>

                <Grid item xs={12}>
                    <Stack spacing={1}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <Typography variant="body2" fontWeight={500} sx={{ fontSize: 12 }}>
                                Bio URL
                            </Typography>
                            <Tooltip title="This is the unique part of your Link in Bio web address.">
                                <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'pointer' }} />
                            </Tooltip>
                        </Stack>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="your-username"
                            value={form.slug}
                            onChange={(e) => {
                                setSlugTouched(true);
                                patchForm({ slug: slugify(e.target.value) });
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment
                                        position="start"
                                        sx={{
                                            mr: 0.25, // reduce the gap
                                            "& .MuiTypography-root": {
                                                color: "text.secondary",
                                                fontWeight: 500,
                                            },
                                        }}
                                    >
                                        {BASE_HOST}/bio/
                                    </InputAdornment>
                                ),

                                endAdornment:(
                                    <InputAdornment position="end">

                                        <Tooltip title="Auto-generate a username">

                                            <IconButton
                                                size="small"
                                                onClick={regenerateSlug}
                                            >
                                                <AutorenewIcon fontSize="small"/>
                                            </IconButton>

                                        </Tooltip>

                                    </InputAdornment>
                                )
                            }}

                            error={slugAvailable === false}
                            helperText={
                                slugChecking
                                ? "Checking..."
                                : slugAvailable===true
                                ? "✓ Available"
                                : slugAvailable===false
                                ? "Already taken — try another or auto-generate"
                                : "Use your own username or auto-generate one"
                            }
                        />
                    </Stack>
                </Grid>

                <Grid item xs={12}>
                    <Stack spacing={1}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <Typography variant="body2" fontWeight={500} sx={{ fontSize: 12 }}>
                                About You
                            </Typography>
                            <Tooltip title="Write a short introduction that visitors will see on your Link in Bio page.">
                                <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'pointer' }} />
                            </Tooltip>
                        </Stack>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            placeholder="Tell people who you are..."
                            value={form.headline}
                            onChange={(e)=>
                                patchForm({
                                    headline:e.target.value
                                })
                            }
                        />
                    </Stack>
                </Grid>

            </Grid>

        </Grid>

    </Grid>

</Paper>
<Paper
    elevation={0}
    sx={{
        p: 2.5,
        borderRadius: 4,
        border: "1px solid #E5E7EB",
        bgcolor: "#fff",
        mb: 2,
    }}
>

    <Box
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        gap={2}
        mb={2}
    >

        <Box>

            <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{ fontSize: { xs: 18, md: 20 } }}
                >
                    Social Links
                </Typography>
                <Tooltip title="Add the platforms you'd like visitors to follow.">
                    <InfoOutlined sx={{ fontSize: 18, color: 'text.secondary', cursor: 'pointer' }} />
                </Tooltip>
            </Stack>

            {/* <Typography color="text.secondary" sx={{ fontSize: { xs: 14, md: 16 }, mt: 0.5 }}>
                Add the platforms you'd like visitors to follow.
            </Typography> */}

        </Box>

        <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={addSocial}
            className="btn btn-primary"
            // sx={{
            //     bgcolor: '#000', color: '#fff', borderRadius: '10px', textTransform: 'none',
            //     fontWeight: 600, px: 3, height: 40, whiteSpace: 'nowrap', flexShrink: 0,
            //     width: { xs: 'auto', sm: 'auto' },
            //     '&:hover': { bgcolor: '#111' },
            // }}
        >
            Add Social
        </Button>

    </Box>

    <Grid container spacing={2}>

        {form.social_links.map((social: any, index: number) => (

            <Grid
                item
                xs={12}
                key={index}
            >

                <Paper
                    elevation={0}
                    sx={{
                        p: 2.5,
                        borderRadius: 3,
                        border: "1px solid #EEF2F7",
                        transition: ".25s",

                        "&:hover": {
                            borderColor: "#2563EB",
                            boxShadow:
                                "0 15px 35px rgba(37,99,235,.08)",
                        },
                    }}
                >

                    <Grid
                        container
                        spacing={2}
                        alignItems="center"
                    >

                        <Grid item xs={12} md={3}>

                            {isOtherRow(index, social.platform) ? (
                                // "Other" mode: the control itself is editable. Type any platform name —
                                // renowned ones (e.g. OnlyFans, Twitch) auto-resolve their logo. Picking a
                                // suggestion from the list snaps back to the fixed predefined option.
                                <Autocomplete
                                    freeSolo
                                    fullWidth
                                    disableClearable
                                    options={SOCIAL_PLATFORMS}
                                    inputValue={social.platform || ''}
                                    onInputChange={(_, val) => setSocial(index, 'platform', val)}
                                    onChange={(_, val) => {
                                        const canon = matchPredefined(val as string);
                                        if (canon) {
                                            setOtherRows((prev) => { const n = new Set(prev); n.delete(index); return n; });
                                            setSocial(index, 'platform', canon);
                                        }
                                    }}
                                    renderOption={(props, option) => (
                                        <li {...props} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            {socialIcon(option)} {option}
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Platform"
                                            className='input-style input-common'
    size="small"    
                                            placeholder="Type a platform name"
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        {socialIcon(social.platform || 'other')}
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            ) : (
                                // Predefined platforms are picked from a fixed list (name not editable).
                                <FormControl  className="w-100 input-style input-common" size="small">
                                    <InputLabel>Platform</InputLabel>
                                    <Select
                                        label="Platform"
                                        value={matchPredefined(social.platform) || ''}
                                        onChange={(e) => handlePlatformSelect(index, e.target.value as string)}
                                        renderValue={(val) => val ? (
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                {socialIcon(val as string)} {val}
                                            </span>
                                        ) : ''}
                                    >
                                        {SOCIAL_PLATFORMS.map((p) => (
                                            <MenuItem key={p} value={p}>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    {socialIcon(p)} {p}
                                                </span>
                                            </MenuItem>
                                        ))}
                                        <MenuItem value="Other">
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                {socialIcon('other')} Other
                                            </span>
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            )}

                        </Grid>

                        <Grid item xs={10} md={8}>

                            <TextField
                                fullWidth
                                label="Bio URL"
                                placeholder="https://..."
                                value={social.url}
                                className='input-style input-common'
                                size="small"
                                onChange={(e)=>
                                    setSocial(
                                        index,
                                        "url",
                                        e.target.value
                                    )
                                }
                                error={!!socialUrlError(social.platform, social.url, matchPredefined(social.platform))}
                                helperText={socialUrlError(social.platform, social.url, matchPredefined(social.platform)) || ' '}
                            />

                        </Grid>

                        <Grid
                            item
                            xs={2}
                            md={1}
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                        >

                            <IconButton
                                color="error"
                                onClick={() =>
                                    removeSocial(index)
                                }
                            >
                                <DeleteOutlineIcon />
                            </IconButton>

                        </Grid>

                    </Grid>

                </Paper>

            </Grid>

        ))}

        {form.social_links.length === 0 && (

            <Grid item xs={12}>

                <Paper
                    variant="outlined"
                    sx={{
                        p: 3,
                        borderRadius: 3,
                        textAlign: "center",
                        borderStyle: "dashed",
                    }}
                >

                    <Language
                        sx={{
                            fontSize: 48,
                            color: "#CBD5E1",
                            mb: 2,
                        }}
                    />

                    <Typography
                        fontWeight={700}
                    >
                        No social links added
                    </Typography>

                    <Typography
                        color="text.secondary"
                        mb={3}
                    >
                        Connect your audience with your social profiles.
                    </Typography>

                    <Button
                        variant="contained"
                        className="btn btn-primary"
                        startIcon={<AddIcon />}
                        onClick={addSocial}
                    >
                        Add First Social
                    </Button>

                </Paper>

            </Grid>

        )}

    </Grid>

</Paper>
{page && (
<Paper
    elevation={0}
    sx={{
        mt: 2,
        p: 2.5,
        borderRadius: 4,
        border: "1px solid #E5E7EB",
    }}
>

    <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        mb={4}
    >

        <Box>

            <Typography
                variant="h5"
                fontWeight={800}
                sx={{ fontSize: { xs: 18, md: 20 } }}
            >
                Content Blocks
            </Typography>

            <Typography color="text.secondary" sx={{ fontSize: { xs: 14, md: 16 } }}>
                Build your Link in Bio page with links,
                text, titles and dividers.
            </Typography>

        </Box>

        <Button
            variant="contained"
            className="btn btn-primary"
            startIcon={<AddIcon />}
            onClick={(e) =>
                setAddMenuEl(e.currentTarget)
            }
            sx={{ flexShrink: 0 }}
        >
            {adding ? "Adding..." : "Add Block"}
        </Button>

    </Stack>

    <Menu
        anchorEl={addMenuEl}
        open={Boolean(addMenuEl)}
        onClose={() => setAddMenuEl(null)}
    >
        {BLOCK_TYPES.map((bt) => (
            <MenuItem
                key={bt.type}
                onClick={() => addBlock(bt.type)}

            >
                <Box
                    display="flex"
                    alignItems="center"
                    gap={2}
                >
                    {bt.icon}

                    <Box>
                        <Typography fontWeight={700}>
                            {bt.label}
                        </Typography>

                        <Typography
                            variant="caption"
                            color="text.secondary"
                        >
                            {bt.desc}
                        </Typography>
                    </Box>
                </Box>
            </MenuItem>
        ))}
    </Menu>

    <Stack spacing={2}>

        {blocks.length === 0 ? (

            <Paper
                variant="outlined"
                sx={{
                    p: 4,
                    borderRadius: 3,
                    borderStyle: "dashed",
                    textAlign: "center",
                }}
            >

                <LinkIcon
                    sx={{
                        fontSize: 50,
                        color: "#CBD5E1",
                        mb: 2,
                    }}
                />

                <Typography
                    fontWeight={700}
                    mb={1}
                >
                    No Content Blocks Yet
                </Typography>

                <Typography
                    color="text.secondary"
                    mb={3}
                >
                    Add links, headings, text or dividers to build your page.
                </Typography>

                <Button
                    variant="contained"
                    className="btn btn-primary"
                    startIcon={<AddIcon />}
                    onClick={(e) =>
                        setAddMenuEl(e.currentTarget)
                    }
                >
                    Add First Block
                </Button>

            </Paper>

        ) : (

            blocks.map((block, index) =>
                blockCard(block, index)
            )

        )}

    </Stack>

</Paper>
)}
<Paper
    elevation={0}
    sx={{
        p: 2.5,
        borderRadius: 4,
        border: "1px solid #E5E7EB",
        bgcolor: "#fff",
        mb: 2,
    }}
>

    <Stack direction="row" spacing={1} alignItems="center" mb={2}>
        <Typography
            variant="h5"
            fontWeight={700}
            sx={{ fontSize: { xs: 18, md: 20 } }}
        >
            Theme & Appearance
        </Typography>
        <Tooltip title="Choose a beautiful theme for your Link in Bio page.">
            <InfoOutlined sx={{ fontSize: 18, color: 'text.secondary', cursor: 'pointer' }} />
        </Tooltip>
    </Stack>

    {/* <Typography
        color="text.secondary"
        mb={2}
        sx={{ fontSize: { xs: 14, md: 16 } }}
    >
        Choose a beautiful theme for your Link in Bio page.
    </Typography> */}

    <Grid container spacing={2}>

        {Object.entries(BIO_THEMES).map(([key, theme]: any) => {

            const active =
                form.theme.bg === theme.bg;

            return (

                <Grid
                    item
                    xs={4}
                    sm={4}
                    md={4}
                    key={key}
                >

                    <Paper
                        onClick={() =>
                            patchForm({
                                theme: {
                                    ...theme,
                                    button_shape:
                                        form.theme.button_shape,
                                },
                            })
                        }
                        sx={{
                            cursor: "pointer",
                            borderRadius: { xs: 2, md: 4 },
                            overflow: "hidden",
                            border: active
                                ? "2px solid #2563EB"
                                : "1px solid #E5E7EB",
                            transition: ".25s",

                            "&:hover": {
                                transform:
                                    "translateY(-5px)",
                                boxShadow:
                                    "0 20px 40px rgba(37,99,235,.12)",
                            },
                        }}
                    >

                        {/* Preview */}

                        <Box
                            sx={{
                                bgcolor: theme.bg,
                                height: { xs: 96, sm: 130, md: 170 },
                                p: { xs: 1, md: 2 },
                            }}
                        >

                            <Box
                                sx={{
                                    width: { xs: 30, sm: 44, md: 60 },
                                    height: { xs: 30, sm: 44, md: 60 },
                                    borderRadius: "50%",
                                    bgcolor: theme.accent,
                                    mx: "auto",
                                    mb: { xs: 1, md: 2 },
                                }}
                            />

                            <Box
                                sx={{
                                    height: { xs: 6, md: 12 },
                                    width: "70%",
                                    bgcolor: theme.text,
                                    opacity: .8,
                                    borderRadius: 10,
                                    mx: "auto",
                                    mb: 1,
                                }}
                            />

                            <Box
                                sx={{
                                    height: { xs: 16, sm: 26, md: 36 },
                                    bgcolor: theme.accent,
                                    borderRadius:
                                        form.theme.button_shape === "pill"
                                            ? 50
                                            : form.theme.button_shape === "square"
                                            ? 1
                                            : 2,
                                    mt: { xs: 1.2, md: 3 },
                                }}
                            />

                        </Box>

                        <Box
                            p={{ xs: 1, md: 2 }}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            flexWrap="wrap"
                            gap={0.5}
                        >

                            <Typography
                                fontWeight={700}
                                sx={{ fontSize: { xs: 13, md: 16 } }}
                            >
                                {theme.label}
                            </Typography>

                            {active && (
                                <Chip
                                    label="Selected"
                                    color="primary"
                                    size="small"
                                    sx={{ display: { xs: "none", sm: "inline-flex" } }}
                                />
                            )}

                        </Box>

                    </Paper>

                </Grid>

            );

        })}

    </Grid>

    {/* Button Style */}

    <Box mt={3}>

        <Typography
            fontWeight={700}
            mb={1.5}
        >
            Button Style
        </Typography>

        <Grid container spacing={2}>

            {[
                "rounded",
                "pill",
                "square",
            ].map((shape) => (

                <Grid
                    item
                    xs={4}
                    key={shape}
                >

                    <Paper
                        onClick={() =>
                            patchForm({
                                theme: {
                                    ...form.theme,
                                    button_shape:
                                        shape,
                                },
                            })
                        }
                        sx={{
                            p: 2,
                            cursor: "pointer",
                            borderRadius: 3,
                            textAlign: "center",
                            border:
                                form.theme.button_shape ===
                                shape
                                    ? "2px solid #2563EB"
                                    : "1px solid #E5E7EB",

                            transition: ".25s",

                            "&:hover": {
                                boxShadow:
                                    "0 15px 30px rgba(0,0,0,.06)",
                            },
                        }}
                    >

                        <Box
                            sx={{
                                height: 40,
                                bgcolor:
                                    form.theme.accent,
                                borderRadius:
                                    shape === "pill"
                                        ? 50
                                        : shape ===
                                          "square"
                                        ? 1
                                        : 2,
                                mb: 2,
                            }}
                        />

                        <Typography
                            fontWeight={700}
                            sx={{ fontSize: { xs: 14, md: 16 } }}
                            textTransform="capitalize"
                        >
                            {shape}
                        </Typography>

                    </Paper>

                </Grid>

            ))}

        </Grid>

    </Box>

</Paper>

</>
    );

    // ===== sticky preview with share bar =====
    const preview = (
        <Box sx={{ position: 'sticky', top: 16 }}>
            {page && (
                <>
                <Box sx={{ mb: 2 }}>
    <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
    >
        <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
        >
            <Typography
                variant="body2"
                fontWeight={600}
            >
                Bio URL
            </Typography>

            <Tooltip title="This is the public link to your Link in Bio page.">
                <InfoOutlined
                    sx={{
                        fontSize: 16,
                        color: "text.secondary",
                        cursor: "pointer",
                    }}
                />
            </Tooltip>
        </Stack>

        <Link
            component="button"
            underline="hover"
            onClick={() => window.open(publicUrl, "_blank")}
            sx={{
                fontSize: 14,
                fontWeight: 600,
            }}
        >
            View Bio
        </Link>
    </Stack>

    <Paper
        variant="outlined"
        sx={{
            p: 1.2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "#FAFAFA",
            borderRadius: 2,
        }}
    >
        <Typography
            variant="body2"
            sx={{
                flexGrow: 1,
                fontFamily: "monospace",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            }}
        >
            {publicUrlDisplay}
        </Typography>

        <Tooltip title={linkCopied ? "Copied!" : "Copy URL"}>
            <IconButton
                size="small"
                onClick={copyLink}
                color={linkCopied ? "success" : "default"}
            >
                <ContentCopyIcon fontSize="small" />
            </IconButton>
        </Tooltip>
    </Paper>
</Box>
               
                {/* <Paper variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5, bgcolor: '#FAFAFA' }}>
                    <Typography variant="caption" sx={{ flexGrow: 1, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{publicUrlDisplay}</Typography>
                    <Tooltip title={linkCopied ? 'Copied!' : 'Copy'}><IconButton size="small" onClick={copyLink} color={linkCopied ? 'success' : 'default'}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Open"><IconButton size="small" onClick={() => window.open(publicUrl, '_blank')}><OpenInNewIcon fontSize="small" /></IconButton></Tooltip>
                </Paper> */}
                 </>
            )}
            <Box sx={{ width: {xs:340,sm: 250, md:260, lg:300}, mx: 'auto', border: '10px solid #111', borderRadius: 6, overflow: 'hidden', height: 600, bgcolor: '#fff', boxShadow: 6 }}>
                <Box sx={{ height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
                    {/* Render at a real phone width (412px, typical Android) then zoom-to-fit
                        the frame. A narrow frame alone makes long link buttons wrap into
                        near-squares, and the 999px "pill" radius then turns them into
                        circles/blobs. Laying out at a true phone width (like the live /bio
                        mobile page) keeps the exact same wrapping + stadium button shapes;
                        `zoom` scales layout AND visuals so scrolling stays correct.
                        Zoom = frame inner width (outer − 20px border) / 412. */}
                    <Box sx={{ width: 412, zoom: { xs: 320 / 412, sm: 230 / 412, md: 240 / 412, lg: 280 / 412 } }}>
                        <BioRender page={{ ...form, social_links: form.social_links, theme: form.theme }} blocks={blocks} />
                    </Box>
                </Box>
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={1}>Live preview</Typography>
        </Box>
    );

    return (
        <Box sx={{mx:'auto', maxWidth:'1400px'}}>
            <ZapMobileHeader title="Zap - Link in Bio" />
            <Box sx={{ px: 3, pt: 3, pb: 1.5 }}>
                {/* During setup, same width and centre as the wizard + preview below
                    (BIO_SETUP_WIDTH); once live it keeps the full page width. */}
                <Box display="flex" alignItems="center" gap={{ xs: 1.5, lg: 2 }} flexWrap="wrap" sx={!page ? { maxWidth: BIO_SETUP_WIDTH, mx: 'auto' } : undefined}>
                    <ZapPageIcon />
                    <Box>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Typography sx={{ fontSize: { xs: 20, lg: 24 }, fontWeight: 700, color: '#111' }}>
                                Link in Bio
                            </Typography>
                            {page && <Chip size="small" color="success" label="Live" />}
                            {page && saveStatus !== 'idle' && (
                                <Typography variant="caption" sx={{ color: saveStatus === 'saving' ? 'text.secondary' : 'success.main', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {saveStatus === 'saving' ? <><CircularProgress size={12} /> Saving…</> : '✓ Saved'}
                                </Typography>
                            )}
                        </Box>
                        <Typography sx={{ color: 'text.secondary', fontSize: { xs: 13, lg: 15 }, mt: 0.5, maxWidth: 650 }}>
                            Set up your page — add a name, photo, bio, socials and theme, then create. You can keep editing everything afterward.
                        </Typography>
                    </Box>
                    <Box sx={{ ml: 'auto' }}>
                        <GuideButton feature="Link in Bio" onClick={() => setGuideOpen(true)} />
                    </Box>
                </Box>
                <FeatureGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} guide={LINK_IN_BIO_GUIDE} />
            </Box>

            {!page ? (
                // OLD: the whole appearance form on first visit — avatar, name, handle,
                // bio, a social repeater and a theme picker at once, and a red
                // "Profile name cannot be blank" before the user had typed anything.
                // POST /bio/setup only needs a slug. Now a 4-step wizard; the form
                // below still powers the editor once the page exists.
                // <Box p={3}>
                //     {alert && <Alert ... />}
                //     <Grid container spacing={3}>
                //         <Grid item xs={12} md={8}>{appearance}<Button onClick={createPage}>Create page</Button></Grid>
                //         <Grid item xs={12} md={4}>{preview}</Grid>
                //     </Grid>
                // </Box>
                <Box p={3}>
                  <Box sx={{ maxWidth: BIO_SETUP_WIDTH, mx: 'auto' }}>
                    <BioWizard
                        influencerId={influencer_id}
                        accountId={account_id}
                        userId={user_id}
                        brandId={UserReducer?.brand_id || null}
                        displayNameHint={creatorDisplayName(UserReducer)}
                        onCreated={async () => {
                            const fresh = await BioService.getMyBioPage(influencer_id, account_id);
                            if (fresh?.data?.rows?.length) hydrate(fresh.data.rows[0], fresh.data.blocks || []);
                            setAlert({ type: 'success', msg: 'Your Link-in-Bio is live!' });
                        }}
                    />
                  </Box>
                </Box>
            ) : (
                <>
                    <Box sx={{ px: 3, py: 2 }}>
                        <Tabs
                            value={tab}
                            onChange={(_, v) => { setTab(v); setAlert(null); }}
                            variant="fullWidth"
                            sx={{
                                minHeight: 32,
                                bgcolor: '#ececf0',
                                borderRadius: '999px',
                                p: '6px',
                                width: '100%',
                                maxWidth: 448,
                                '& .MuiTabs-indicator': { display: 'none' },
                            }}
                        >
                            {['Links', 'Appearance', 'Analytics'].map(label => (
                                <Tab
                                    key={label}
                                    label={label}
                                    sx={{
                                        minHeight: 20,
                                        textTransform: 'none',
                                        fontSize: 12,
                                        fontWeight: 500,
                                        borderRadius: '999px',
                                        color: '#000',
                                        lineHeight: 0.85,
                                        px: 1.5,
                                        minWidth: 0,
                                        '&:hover': {
                                            bgcolor: '#000',
                                            color: '#fff',
                                        },
                                        '&.Mui-selected': {
                                            bgcolor: '#000',
                                            color: '#fff',
                                            fontWeight: 600,
                                        },
                                    }}
                                />
                            ))}
                        </Tabs>
                    </Box>
                    {tab === 2 ? (
                        <Box p={3}><BioAnalytics influencer_id={influencer_id} account_id={account_id} /></Box>
                    ) : (
                        <Box p={3}>
                            {alert && <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>{alert.msg}</Alert>}
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={7}>{tab === 0 ? linksEditor : appearance}</Grid>
                                <Grid item xs={12} md={5}>{preview}</Grid>
                            </Grid>
                        </Box>
                    )}
                </>
            )}

            {uploadBlockId !== null && (
                <FileUploadPopup openModal={uploadBlockId !== null} onClose={() => setUploadBlockId(null)} acceptedFileTypes=".jpg, .jpeg, .png" path={uploadPath} coverImage={false}
                    onSend={(e: { url: string }) => { if (uploadBlockId !== null) editBlock(uploadBlockId, { image_url: e.url }, true); setUploadBlockId(null); }} />
            )}
        </Box>
    );
};

const mapStateToProps = (state: any) => ({ UserReducer: state.UserReducer.UserData });
export const LinkInBio = connect(mapStateToProps)(LinkInBioComponent);
